import { useState } from 'react';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Eye, Pencil } from 'lucide-react';

import { Modal } from '$layouts/v2/components/Modal.jsx';
import { Stack } from '$layouts/v2/components/Stack.jsx';

import { FieldListEditor } from './FieldListEditor';
import { cloneField, fieldSummary } from './fieldSchema';

export function FieldsEditor({
  label,
  fields,
  inputTypes,
  onChange,
  readOnly = false,
  reservedNames = [],
}: {
  label: string;
  fields: v4.FieldSpec[];
  inputTypes: v4.FieldInputType[];
  onChange: (fields: v4.FieldSpec[]) => void;
  /** Opens the field definitions for inspection without allowing changes. */
  readOnly?: boolean;
  /** Runtime-owned field names that cannot be declared in this editor. */
  reservedNames?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<v4.FieldSpec[]>([]);

  const openEditor = () => {
    setDraft(fields.map((field) => cloneField(field)));
    setOpen(true);
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {fields.length === 0
                ? 'No fields configured yet.'
                : `${fields.length} ${
                    fields.length === 1 ? 'field' : 'fields'
                  } configured.`}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={readOnly ? <Eye size={14} /> : <Pencil size={14} />}
            onClick={openEditor}
          >
            {readOnly ? 'View fields' : 'Edit fields'}
          </Button>
        </Stack>
        {fields.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{ mt: 1.25, flexWrap: 'wrap' }}
          >
            {fields.slice(0, 3).map((field, index) => (
              <Chip
                key={`${field.name}-${index}`}
                size="small"
                label={fieldSummary(field)}
              />
            ))}
            {fields.length > 3 && (
              <Chip
                size="small"
                variant="outlined"
                label={`+${fields.length - 3} more`}
              />
            )}
          </Stack>
        )}
      </Paper>

      {open && (
        <Modal
          title={label}
          onClose={() => setOpen(false)}
          maxWidth="md"
          actions={
            <>
              <Button onClick={() => setOpen(false)}>
                {readOnly ? 'Close' : 'Cancel'}
              </Button>
              {!readOnly && (
                <Button variant="contained" onClick={apply}>
                  Apply fields
                </Button>
              )}
            </>
          }
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {readOnly
              ? 'These are the fields this workflow collects or returns. Field names are used in templates and mappings; display labels are what people see.'
              : 'Set up the information this workflow collects or returns. A field name is used in templates and mappings; its display label is what people see.'}
          </Typography>
          <FieldListEditor
            fields={draft}
            inputTypes={inputTypes}
            onChange={setDraft}
            readOnly={readOnly}
            reservedNames={reservedNames}
          />
        </Modal>
      )}
    </Box>
  );
}
